import React, { useState, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {
  getAllServicios,
  insertServicio,
  updateServicio,
  deleteServicio,
  uploadImageAppCliente,
} from "../../../api/appClientesApi";
import "./appclientes.css";

export default function Servicios() {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef();

  // 🔹 Función para limpiar input file sin errores
  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const LIMITS = {
    titulo: 45,
    descripcion: 100,
  };
  const IMAGE = {
    maxSizeMB: 1,
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
  };

  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    link: "",
    imagenurl: "",
  });

  // 🔹 Cargar servicios
  const loadServicios = async () => {
    try {
      const res = await getAllServicios();
      setData(res || []);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  useEffect(() => {
    loadServicios();
  }, []);

  // 🔹 Guardar servicio
const handleSave = async () => {
  try {
    // 🔹 Validar límites solamente (pero no obligatorio)
    if (formData.titulo.length > LIMITS.titulo) {
      return Swal.fire(
        "Límite superado",
        `El título no puede superar ${LIMITS.titulo} caracteres.`,
        "warning"
      );
    }

    if (formData.descripcion.length > LIMITS.descripcion) {
      return Swal.fire(
        "Límite superado",
        `La descripción no puede superar ${LIMITS.descripcion} caracteres.`,
        "warning"
      );
    }

    // Validar imagen si hay nueva
    const file = fileInputRef.current?.files?.[0];
    if (file) {
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > IMAGE.maxSizeMB) {
        return Swal.fire(
          "Imagen demasiado grande",
          `El archivo debe pesar menos de ${IMAGE.maxSizeMB} MB.`,
          "error"
        );
      }
      if (!IMAGE.allowedTypes.includes(file.type)) {
        return Swal.fire(
          "Formato inválido",
          "Solo se permiten imágenes JPG, PNG o WEBP.",
          "error"
        );
      }
    }

    Swal.fire({
      title: "Guardando...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    let imagenurl = formData.imagenurl;
    if (file) {
      const uploadRes = await uploadImageAppCliente(file);
      imagenurl = uploadRes.filename;
    }

    const payload = {
      titulo: formData.titulo.trim(),  // 👉 Ahora puede ser vacío
      descripcion: formData.descripcion.trim(),
      link: formData.link?.trim() || "",
      imagenurl,
      idservicio: selectedItem?.idservicio || null,
    };

    if (selectedItem) {
      await updateServicio(payload);
      Swal.fire("Actualizado", "Servicio actualizado correctamente", "success");
    } else {
      await insertServicio(payload);
      Swal.fire("Agregado", "Servicio agregado correctamente", "success");
    }

    setShowModal(false);
    setSelectedItem(null);
    setImagePreview(null);
    clearFileInput();
    loadServicios();
  } catch (err) {
    Swal.fire("Error", err.message, "error");
  }
};


  // 🔹 Eliminar servicio
  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "¿Eliminar servicio?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (confirm.isConfirmed) {
      try {
        await deleteServicio(id);
        Swal.fire("Eliminado", "Servicio eliminado correctamente", "success");
        loadServicios();
      } catch (err) {
        Swal.fire("Error", err.message, "error");
      }
    }
  };

  // 🔹 Handle vista previa imagen
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeMB = file.size / 1024 / 1024;

    if (sizeMB > IMAGE.maxSizeMB) {
      Swal.fire("Imagen demasiado grande", `Máximo ${IMAGE.maxSizeMB} MB.`, "error");
      clearFileInput();
      return;
    }

    if (!IMAGE.allowedTypes.includes(file.type)) {
      Swal.fire("Formato inválido", "Solo JPG, PNG o WEBP.", "error");
      clearFileInput();
      return;
    }

    setImagePreview(URL.createObjectURL(file));
  };

  // 🔹 Columnas tabla
  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "idservicio", className: "text-center", width: 60 },
      { Header: "Título", accessor: "titulo" },
      { Header: "Descripción", accessor: "descripcion" },
      {
        Header: "Link",
        accessor: "link",
        Cell: ({ value }) =>
          value ? (
            <a href={value} target="_blank" rel="noreferrer">
              {value}
            </a>
          ) : (
            "-"
          ),
      },
      {
        Header: "Imagen",
        accessor: "imagenurl",
        Cell: ({ value }) =>
          value ? (
            <img
              src={`https://eethsa.xyz/eethsa1/Uploads/AppClientes/${value}`}
              alt="preview"
              style={{ width: 80, borderRadius: 8 }}
            />
          ) : (
            "-"
          ),
      },
      {
        Header: "Opciones",
        Cell: ({ row }) => (
          <div className="text-center">
            <button
              className="btn btn-outline-primary btn-sm me-1"
              onClick={() => {
                setSelectedItem(row.original);
                setImagePreview(
                  row.original.imagenurl
                    ? `https://eethsa.xyz/eethsa1/Uploads/AppClientes/${row.original.imagenurl}`
                    : null
                );
                clearFileInput();
                setShowModal(true);
              }}
            >
              <i className="fas fa-pen"></i>
            </button>

            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => handleDelete(row.original.idservicio)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // 🔹 Config react-table
  const table = useTable(
    { columns, data, initialState: { pageSize: 10 } },
    useFilters,
    useGlobalFilter,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    state: { globalFilter },
  } = table;

  // 🔹 Rellenar form cuando se edita
  useEffect(() => {
    if (selectedItem) {
      setFormData({
        titulo: selectedItem.titulo,
        descripcion: selectedItem.descripcion,
        link: selectedItem.link,
        imagenurl: selectedItem.imagenurl,
      });
    } else {
      setFormData({ titulo: "", descripcion: "", link: "", imagenurl: "" });
    }
  }, [selectedItem]);

  return (
    <div className="mt-3">
      {/* 🔹 Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedItem(null);
            setImagePreview(null);
            clearFileInput();
            setShowModal(true);
          }}
        >
          + Agregar Servicio
        </button>

        <input
          type="text"
          placeholder="Buscar..."
          value={globalFilter || ""}
          className="form-control"
          style={{ width: 250 }}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </div>

      {/* 🔹 Tabla */}
      <div className="tabla-scroll">
        <table {...getTableProps()} className="table table-sm tabla-app">
          <thead>
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col) => (
                  <th {...col.getHeaderProps()}>{col.render("Header")}</th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 🔹 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedItem ? "Editar Servicio" : "Nuevo Servicio"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Título</Form.Label>
              <Form.Control
                value={formData.titulo}
                maxLength={LIMITS.titulo}
                onChange={(e) =>
                  setFormData({ ...formData, titulo: e.target.value })
                }
              />
              <small className="text-muted">
                {formData.titulo.length}/{LIMITS.titulo} caracteres
              </small>
            </Form.Group>

            <Form.Group className="mt-2">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={LIMITS.descripcion}
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
              />
              <small className="text-muted">
                {formData.descripcion.length}/{LIMITS.descripcion} caracteres
              </small>
            </Form.Group>

            <Form.Group className="mt-2">
              <Form.Label>Link</Form.Label>
              <Form.Control
                value={formData.link}
                onChange={(e) =>
                  setFormData({ ...formData, link: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Imagen (máx. 1 MB)</Form.Label>
              <Form.Control
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageChange}
              />

              {imagePreview && (
                <div className="text-center mt-3">
                  <img
                    src={imagePreview}
                    alt="preview"
                    style={{ maxWidth: "100%", borderRadius: 10 }}
                  />
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="danger" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
